import { useState } from 'react';
import { products, productCategories, type Product } from '../../../mocks/products';

const goToContact = (name?: string) => {
  const el = document.getElementById('contact');
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  if (name) {
    setTimeout(() => {
      const sel = document.querySelector<HTMLSelectElement>('select[name="products_interest"]');
      if (sel) sel.value = name;
    }, 700);
  }
};

const ProductCard = ({ p, index }: { p: Product; index: number }) => (
  <div
    className="group relative overflow-hidden cursor-pointer bg-[#080808] border border-white/[0.06] hover:border-[#c9a84c]/40 transition-all duration-500"
    style={{ animationDelay: `${index * 40}ms` }}
    onClick={() => goToContact(p.name)}
  >
    {/* Image */}
    <div className="relative overflow-hidden" style={{ height: '220px' }}>
      <img
        src={p.image}
        alt={p.name}
        className="w-full h-full object-cover object-top group-hover:scale-[1.07] transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/20 to-transparent" />

      {/* Badges */}
      <div className="absolute top-3 left-3 flex flex-col gap-1.5">
        {p.isNew && (
          <span className="px-2.5 py-1 bg-[#c9a84c] text-[#030303] text-[8px] font-black tracking-[0.18em]">NEW</span>
        )}
        {p.isFeatured && !p.isNew && (
          <span className="px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/70 text-[8px] font-black tracking-[0.14em] border border-white/10">FEATURED</span>
        )}
      </div>

      {/* Hover CTA overlay */}
      <div className="absolute inset-0 bg-[#c9a84c]/0 group-hover:bg-[#c9a84c]/8 transition-all duration-500 flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
          <div className="flex items-center gap-2 px-5 py-2.5 bg-[#c9a84c] text-[#030303] text-[9px] font-black tracking-[0.18em]">
            REQUEST INFO <i className="ri-arrow-right-line text-[10px]" />
          </div>
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="p-5">
      <span className="block text-[#c9a84c] text-[8px] tracking-[0.24em] font-black uppercase mb-2">{p.useCase}</span>
      <h4
        className="text-white font-bold text-[13px] leading-snug mb-3 group-hover:text-[#c9a84c] transition-colors duration-300"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {p.name}
      </h4>
      <p className="text-white/30 text-[11px] leading-[1.75] line-clamp-2 mb-4">{p.description}</p>

      <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
        <span className="text-white/15 text-[8px] tracking-[0.12em] font-black">NANOPRO®</span>
        <div className="w-6 h-6 flex items-center justify-center border border-[#c9a84c]/20 group-hover:bg-[#c9a84c] group-hover:border-[#c9a84c] transition-all duration-200">
          <i className="ri-arrow-right-line text-[#c9a84c] text-[10px] group-hover:text-[#030303]" />
        </div>
      </div>
    </div>
  </div>
);

interface ProductShowcaseProps {
  onCategoryVisible: (id: string) => void;
}

const ProductShowcase = ({ onCategoryVisible: _unused }: ProductShowcaseProps) => {
  const allCatId = 'all';
  const [activeCategory, setActiveCategory] = useState<string>(allCatId);

  const allCategories = [
    { id: allCatId, label: 'All Products', anchor: '' },
    ...productCategories,
  ];

  const filteredProducts =
    activeCategory === allCatId
      ? products
      : products.filter((p) => p.category === activeCategory);

  const activeCatLabel =
    activeCategory === allCatId
      ? 'All Products'
      : productCategories.find((c) => c.id === activeCategory)?.label ?? '';

  return (
    <section id="products" className="bg-[#050505] py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-14">

        {/* ── Section Header ── */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-10 bg-[#c9a84c]" />
            <p className="text-[#c9a84c] text-[10px] tracking-[0.32em] font-black">COMPLETE PRODUCT LINEUP</p>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <h2
              className="text-white font-black leading-[0.93]"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(36px, 5.5vw, 64px)', letterSpacing: '-0.03em' }}
            >
              The Complete<br />
              <span style={{ WebkitTextStroke: '1.5px rgba(255,255,255,0.22)', color: 'transparent' }}>Product Catalog</span>
            </h2>
            <p className="text-white/30 text-[13px] max-w-[380px] leading-[1.85]">
              All approved NanoPro® products organized by category. Contact Surface Solutions of CT for guidance on choosing the right formulation.
            </p>
          </div>
        </div>

        {/* ── Category Filter Tabs ── */}
        <div className="flex flex-wrap gap-2 mb-12">
          {allCategories.map((cat) => {
            const count = cat.id === allCatId ? products.length : products.filter((p) => p.category === cat.id).length;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-[10px] font-black tracking-[0.14em] transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-[#c9a84c] text-[#030303] border-[#c9a84c]'
                    : 'bg-transparent text-white/40 border-white/10 hover:border-[#c9a84c]/30 hover:text-white/70'
                }`}
              >
                {cat.label.toUpperCase()}
                <span
                  className={`text-[8px] px-1.5 py-0.5 font-black ${
                    isActive ? 'bg-[#030303]/20 text-[#030303]' : 'bg-white/8 text-white/30'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Results Label ── */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-5 bg-[#c9a84c]" />
            <span className="text-white/50 text-[11px] tracking-[0.1em]">
              <span className="text-white font-bold">{filteredProducts.length}</span> products in{' '}
              <span className="text-[#c9a84c] font-bold">{activeCatLabel}</span>
            </span>
          </div>
          <div className="h-px flex-1 mx-6 bg-gradient-to-r from-white/6 to-transparent" />
        </div>

        {/* ── Product Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} />
          ))}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-16 pt-14 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white/50 text-[12px] mb-1 tracking-[0.06em]">AUTHORIZED RESELLER</p>
            <p className="text-white/28 text-[13px] max-w-[440px] leading-relaxed">
              All products available through Surface Solutions of CT. Contact us for availability, product guidance, and dealer purchasing.
            </p>
          </div>
          <button
            onClick={() => goToContact()}
            className="flex-shrink-0 px-9 py-4 bg-[#c9a84c] text-[#030303] text-[11px] font-black tracking-[0.18em] hover:bg-[#e0bf6a] transition-colors cursor-pointer whitespace-nowrap"
          >
            INQUIRE ABOUT PRODUCTS
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
